import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { CalendarStar, MapPin, ListChecks, ArrowRight, NotePencil } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import NoteViewerDialog from "@/components/NoteViewerDialog";

/**
 * ActivitiesTab — dedicated view of every `note_type == "activity_briefing"`
 * record scoped to a Client (all engagements) or a specific Engagement.
 *
 * Iter 85.34 · Activity Briefings remain regular Notes in the database
 * (single canonical record, no parallel content store) — this tab is
 * simply a filtered projection of `/notes?note_type=activity_briefing`
 * plus a lookup against `/tasks?activity_briefing_id=<note_id>` so the
 * partner can see follow-through counts without opening each activity.
 *
 * Clicking a row opens the underlying Note in the standard
 * `NoteViewerDialog` — same working surface as any other meeting note.
 *
 * @param {'client'|'engagement'} scope  Determines which id to filter by.
 * @param {string} clientId              Present when scope='client'.
 * @param {string} engagementId          Present when scope='engagement'.
 * @param {Array}  engagements           Optional: list of engagements the
 *                                        parent already loaded, used to
 *                                        show the engagement badge on each
 *                                        row in the client scope. Empty
 *                                        list is fine (badge just hides).
 */
const SERVICE_LABEL = {
  trusted_advisor: "Trusted Advisor",
  strategic_initiative: "Strategic Initiative",
  executive_convening: "Executive Convening",
  executive_communication: "Executive Communication",
};

export default function ActivitiesTab({ scope, clientId, engagementId, engagements = [] }) {
  const [notes, setNotes] = useState([]);
  const [tasksByActivity, setTasksByActivity] = useState({});
  // Iter 85.41 · Meeting-note count per activity — surfaces "activity
  // thickness" so partners can see at a glance how much working
  // material has accrued around each discrete activity without
  // opening the record.
  const [meetingNotesByActivity, setMeetingNotesByActivity] = useState({});
  const [openNote, setOpenNote] = useState(null);

  useEffect(() => {
    const params = { note_type: "activity_briefing" };
    if (scope === "client") params.client_id = clientId;
    if (scope === "engagement") params.engagement_id = engagementId;
    api.get("/notes", { params }).then((r) => setNotes(r.data || []));
  }, [scope, clientId, engagementId]);

  useEffect(() => {
    // Iter 85.34 · Fetch canonical Tasks for the scope so we can show a
    // per-activity follow-through count. Uses the same `/tasks` endpoint
    // the Engagement Tasks tab uses — no duplicate index.
    const params = {};
    if (scope === "client") params.client_id = clientId;
    if (scope === "engagement") params.engagement_id = engagementId;
    api.get("/tasks", { params }).then((r) => {
      const map = {};
      (r.data || []).forEach((t) => {
        if (!t.activity_briefing_id) return;
        (map[t.activity_briefing_id] = map[t.activity_briefing_id] || { open: 0, done: 0 });
        if (t.status === "done") map[t.activity_briefing_id].done += 1;
        else map[t.activity_briefing_id].open += 1;
      });
      setTasksByActivity(map);
    }).catch(() => setTasksByActivity({}));
  }, [scope, clientId, engagementId, openNote]);

  useEffect(() => {
    // Iter 85.41 · Fetch every Meeting Note in this scope in one call,
    // then group by activity_briefing_id so each activity row can
    // surface "N meeting notes" without a per-row fan-out. A single
    // list request scales fine — advisory scope tops out at hundreds
    // of notes per client.
    const params = {};
    if (scope === "client") params.client_id = clientId;
    if (scope === "engagement") params.engagement_id = engagementId;
    api.get("/notes", { params }).then((r) => {
      const counts = {};
      (r.data || []).forEach((nn) => {
        if (nn.activity_briefing_id) counts[nn.activity_briefing_id] = (counts[nn.activity_briefing_id] || 0) + 1;
      });
      setMeetingNotesByActivity(counts);
    }).catch(() => setMeetingNotesByActivity({}));
  }, [scope, clientId, engagementId, openNote]);

  const engById = useMemo(() => {
    const m = {};
    engagements.forEach((e) => { m[e.id] = e; });
    return m;
  }, [engagements]);

  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
    catch { return ""; }
  };

  if (notes.length === 0) {
    return (
      <div className="border border-dashed border-border p-12 text-center" data-testid="activities-empty">
        <CalendarStar size={28} weight="duotone" className="text-brass mx-auto mb-3" />
        <div className="text-sm text-foreground">No activities yet</div>
        <div className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
          Activity briefings capture the discrete dinners, convenings, workshops, or roundtables that happen inside this{" "}
          {scope === "client" ? "partner org relationship" : "engagement"}. Start one from the Advisory Desk.
        </div>
        <Button asChild variant="outline" className="rounded-none mt-4" data-testid="activities-empty-cta">
          <a href="/notes">Open Advisory Desk →</a>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="activities-tab">
      {notes.map((n) => {
        const taskCounts = tasksByActivity[n.id] || { open: 0, done: 0 };
        const eng = scope === "client" && n.engagement_id ? engById[n.engagement_id] : null;
        return (
          <button
            key={n.id}
            type="button"
            onClick={() => setOpenNote(n)}
            className="w-full text-left border border-border bg-card hover:border-primary/60 hover:bg-secondary/30 transition-colors p-4 group"
            data-testid={`activity-row-${n.id}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-primary">
                  <CalendarStar size={14} weight="fill" />
                  <span className="font-medium text-sm truncate" data-testid={`activity-title-${n.id}`}>{n.title}</span>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[0.6875rem] font-mono uppercase tracking-widest text-muted-foreground">
                  {n.activity_date && <span data-testid={`activity-date-${n.id}`}>{fmtDate(n.activity_date)}</span>}
                  {n.activity_service_category && (
                    <span className="text-brass" data-testid={`activity-service-${n.id}`}>
                      {SERVICE_LABEL[n.activity_service_category] || n.activity_service_category}
                    </span>
                  )}
                  {n.activity_location && (
                    <span className="flex items-center gap-1" data-testid={`activity-location-${n.id}`}>
                      <MapPin size={10} /> {n.activity_location}
                    </span>
                  )}
                  {eng && (
                    <span className="text-primary" data-testid={`activity-engagement-${n.id}`}>{eng.name}</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {(meetingNotesByActivity[n.id] || 0) > 0 && (
                  <div
                    className="flex items-center gap-1 text-[0.6875rem] font-mono uppercase tracking-widest text-brass"
                    data-testid={`activity-notes-count-${n.id}`}
                    title={`${meetingNotesByActivity[n.id]} meeting note${meetingNotesByActivity[n.id] === 1 ? "" : "s"} linked to this activity`}
                  >
                    <NotePencil size={12} weight="bold" />
                    <span className="text-foreground">{meetingNotesByActivity[n.id]}</span>
                    <span>meeting note{meetingNotesByActivity[n.id] === 1 ? "" : "s"}</span>
                  </div>
                )}
                {(taskCounts.open > 0 || taskCounts.done > 0) && (
                  <div className="flex items-center gap-1 text-[0.6875rem] font-mono uppercase tracking-widest text-muted-foreground" data-testid={`activity-tasks-${n.id}`}>
                    <ListChecks size={12} weight="bold" />
                    <span className="text-foreground">{taskCounts.open}</span>
                    <span>open</span>
                    {taskCounts.done > 0 && (<><span className="mx-1">·</span><span>{taskCounts.done} done</span></>)}
                  </div>
                )}
                <ArrowRight size={14} className="text-muted-foreground group-hover:text-primary" />
              </div>
            </div>
          </button>
        );
      })}

      {openNote && (
        <NoteViewerDialog
          note={openNote}
          open={!!openNote}
          onOpenChange={(v) => { if (!v) setOpenNote(null); }}
          onNoteChange={() => {
            // Refresh the scoped notes list + task counts so an inline
            // edit or task promotion inside the viewer shows up in the
            // tab immediately after close.
            const params = { note_type: "activity_briefing" };
            if (scope === "client") params.client_id = clientId;
            if (scope === "engagement") params.engagement_id = engagementId;
            api.get("/notes", { params }).then((r) => setNotes(r.data || []));
          }}
        />
      )}
    </div>
  );
}
